/**
 * The maths behind Reports > Surveys. Pure functions of a round's results, so
 * every number on the page is tested here rather than in the page.
 *
 *   - "Positive" is the share of a rating's real answers in its top two
 *     ("Good" or "Excellent" of four). Not-applicable and skipped answers are
 *     left out, and every share says how many answers it counted.
 *   - NPS is the usual one for a 0-10 question: % scoring 9-10 minus % scoring
 *     0-6, from -100 to 100.
 *   - A submission is grouped by the department and partner team stored on it
 *     when it arrived, which a sent round's design can no longer change.
 *   - Board members answer from their own department too. forAudience reads
 *     a round as the Board alone, or everyone else, with every count to match.
 *   - A department split into areas (CCD Front Office, CCD Back Office) has
 *     its answers counted under the team of the area each person chose. Its
 *     areas are addressed as "department:area" wherever departments are.
 */
import type { SurveyQuestion, SurveyRoundResults, SurveySubmission, SurveyTypeResults } from '../types'

/** Where an answer was given from: its department, or "department:area"
 *  in a split department. */
export const slotOf = (s: SurveySubmission) => (s.area ? `${s.departmentId}:${s.area}` : s.departmentId)

/** A department, or an area of one, by name: "Finance", "CCD Back Office". */
export function placeName(round: SurveyRoundResults, slot: string): string {
  const [departmentId, areaId] = slot.split(':')
  const department = round.departments.find(d => d.id === departmentId)
  const area = areaId ? department?.areas?.find(a => a.id === areaId) : undefined
  return department ? (area ? `${department.name} ${area.name}` : department.name) : ''
}

/** The top of a question's scale: the number of stars, or 10. */
export const scaleMax = (q: SurveyQuestion) => (q.kind === 'rating' ? q.labels.length : 10)

export function scores(submissions: SurveySubmission[], questionId: string): number[] {
  return submissions
    .map(s => s.answers[questionId])
    .filter((v): v is number => typeof v === 'number')
}

export interface Nps {
  score: number | null
  promoters: number
  passives: number
  detractors: number
  n: number
}

export function nps(values: number[]): Nps {
  const promoters = values.filter(v => v >= 9).length
  const detractors = values.filter(v => v <= 6).length
  const n = values.length
  return {
    score: n ? Math.round(((promoters - detractors) / n) * 100) : null,
    promoters,
    passives: n - promoters - detractors,
    detractors,
    n,
  }
}

/** How many answered each star (index 0 = 1 star, or 0 on a 0-10 scale),
 *  plus not-applicable. */
export function distribution(submissions: SurveySubmission[], q: SurveyQuestion): { counts: number[]; na: number } {
  const counts = Array.from({ length: scaleMax(q) + (q.kind === 'scale' ? 1 : 0) }, () => 0)
  let na = 0
  for (const s of submissions) {
    const v = s.answers[q.id]
    if (v === 'na') na++
    else if (typeof v === 'number') counts[q.kind === 'scale' ? v : v - 1]++
  }
  return { counts, na }
}

const sum = (record: Record<string, number>, keys?: string[]) =>
  (keys ?? Object.keys(record)).reduce((total, key) => total + (record[key] ?? 0), 0)

export interface ResponseRate {
  responded: number
  invited: number
  pct: number | null
}

/** Answers received of the people sent their link, for some departments (or
 *  all). Everyone in a split department could have chosen any of its areas,
 *  so an area counts the whole department's people and its own answers. */
export function responseRate(round: SurveyRoundResults, departmentIds?: string[]): ResponseRate {
  const departments = departmentIds && [...new Set(departmentIds.map(id => id.split(':')[0]))]
  const invited = sum(round.invited, departments)
  const responded = departmentIds
    ? departmentIds.reduce((n, id) => n + (id.includes(':')
      ? round.submissions.filter(s => slotOf(s) === id).length
      : round.responded[id] ?? 0), 0)
    : sum(round.responded)
  return { responded, invited, pct: invited ? Math.round((responded / invited) * 100) : null }
}

/** The lowest star counted as positive: the top two of a rating, so "Good"
 *  and "Excellent" of four. */
export const positiveFrom = (q: SurveyQuestion) => Math.max(1, q.labels.length - 1)

export interface Share {
  /** Whole percent, null when nothing was counted. */
  pct: number | null
  count: number
  n: number
}

const share = (count: number, n: number): Share => ({ pct: n ? Math.round((count / n) * 100) : null, count, n })

/** Of the real scores a rating got, how many were positive. */
export function positive(submissions: SurveySubmission[], q: SurveyQuestion): Share {
  const values = scores(submissions, q.id)
  return share(values.filter(v => v >= positiveFrom(q)).length, values.length)
}

/** Positive across every rating in the round: one figure for a round, a team
 *  or a department, weighting each answer the same. */
export function positiveOverall(round: SurveyRoundResults, submissions = round.submissions): Share {
  let count = 0
  let n = 0
  for (const q of round.questions.filter(x => x.kind === 'rating')) {
    const s = positive(submissions, q)
    count += s.count
    n += s.n
  }
  return share(count, n)
}

/** How every rating in the round was answered, best answer first, weighting
 *  each answer the same as `positiveOverall` does. Ratings that share their
 *  labels are split answer by answer; mixed labels only split into positive
 *  and the rest, since "Good" on one scale is not "Good" on another. */
export function answerSplit(round: SurveyRoundResults, submissions = round.submissions): { label: string; count: number; positive: boolean }[] {
  const ratings = round.questions.filter(q => q.kind === 'rating')
  const labels = ratings[0]?.labels ?? []
  if (ratings.every(q => q.labels.join('\n') === labels.join('\n'))) {
    const counts = labels.map(() => 0)
    for (const q of ratings) distribution(submissions, q).counts.forEach((n, i) => { counts[i] += n })
    return labels.map((label, i) => ({ label, count: counts[i], positive: i + 1 >= positiveFrom(ratings[0]) })).reverse()
  }
  const all = positiveOverall(round, submissions)
  return [
    { label: 'Positive', count: all.count, positive: true },
    { label: 'Other answers', count: all.n - all.count, positive: false },
  ]
}

/** What "positive" means in words: "Good or Excellent" when every rating
 *  shares its top two labels, otherwise the general rule. */
export function positiveMeaning(round: SurveyRoundResults): string {
  const tops = new Set(round.questions.filter(q => q.kind === 'rating').map(q => q.labels.slice(-2).join(' or ')))
  return tops.size === 1 ? [...tops][0] : 'one of the top two answers'
}

/** How a choice question was answered, option by option. */
export function choiceShares(submissions: SurveySubmission[], q: SurveyQuestion): { option: string; share: Share }[] {
  const answers = submissions.map(s => s.answers[q.id]).filter(v => typeof v === 'string' && v !== 'na')
  return q.options.map(option => ({ option, share: share(answers.filter(a => a === option).length, answers.length) }))
}

export interface Group {
  key: string
  name: string
  /** The departments it supports, and "department:area" for the areas of
   *  split ones. */
  departmentIds: string[]
  /** A partner team, rather than a department that has none. */
  partnered: boolean
}

/**
 * What a round's results are grouped by: its partner teams, each with the
 * departments it looks after, then every department with no partner team on
 * its own under its own name. A partner team nobody was asked about from (End
 * User Services) is left out: its only score is its own question. With no
 * partner teams at all, that is simply one group per department.
 */
export function groups(round: SurveyRoundResults): Group[] {
  const partnerIds = new Set(round.partners.map(p => p.id))
  const teams = round.partners
    .map(p => ({
      key: p.id,
      name: p.name,
      departmentIds: [
        ...round.departments.filter(d => !d.areas?.length && d.partnerId === p.id).map(d => d.id),
        ...round.departments.flatMap(d => (d.areas ?? []).filter(a => a.partnerId === p.id).map(a => `${d.id}:${a.id}`)),
      ],
      partnered: true,
    }))
    .filter(g => g.departmentIds.length > 0)
  const alone = round.departments
    .filter(d => !d.areas?.length && !partnerIds.has(d.partnerId))
    .map(d => ({ key: d.id, name: d.name, departmentIds: [d.id], partnered: false }))
  return [...teams, ...alone]
}

/** Whether a round has partner teams to group by (rather than departments). */
export const byTeams = (round: SurveyRoundResults) =>
  round.partners.some(p => round.departments.some(d => d.partnerId === p.id || d.areas?.some(a => a.partnerId === p.id)))

/** The partner team a submission was about, or '' for none. */
export const partnerName = (round: SurveyRoundResults, partnerId: string) =>
  round.partners.find(p => p.id === partnerId)?.name ?? ''

/** The weakest rating for any group, among those with at least `minAnswers`
 *  answers so one person's view isn't singled out. */
export function weakest(round: SurveyRoundResults, minAnswers = 3): { group: Group; question: SurveyQuestion; positive: Share } | null {
  let worst: { group: Group; question: SurveyQuestion; positive: Share } | null = null
  for (const group of groups(round)) {
    const subs = round.submissions.filter(s => group.departmentIds.includes(slotOf(s)))
    for (const question of round.questions.filter(q => q.kind === 'rating')) {
      const p = positive(subs, question)
      if (p.pct === null || p.n < minAnswers) continue
      if (!worst || p.pct < (worst.positive.pct ?? 101)) worst = { group, question, positive: p }
    }
  }
  return worst
}

/** A group's line on the chart over time: a team by its id, a department
 *  without one by its own, so a team and a department that share a name
 *  stay two lines. */
export const seriesKey = (group: Group) => `${group.partnered ? 'team' : 'department'}:${group.key}`

/** One round on the chart over time. Groups are kept apart from the round's
 *  name and overall, so a team or department called "Overall" or "round"
 *  can't overwrite them. */
export interface OverTimeRow {
  round: string
  overall: number | null
  /** Positive per group, by seriesKey. */
  groups: Map<string, number | null>
}

/** Positive per round, overall and per group, for a chart over time, with
 *  each group's line (its key and name) in the order first seen. */
export function positiveOverTime(results: SurveyTypeResults): { rows: OverTimeRow[]; series: { key: string; name: string }[] } {
  const series: { key: string; name: string }[] = []
  const rows = results.rounds.map(round => {
    const byGroup = new Map<string, number | null>()
    for (const group of groups(round)) {
      const key = seriesKey(group)
      if (!series.some(s => s.key === key)) series.push({ key, name: group.name })
      byGroup.set(key, positiveOverall(round, round.submissions.filter(s => group.departmentIds.includes(slotOf(s)))).pct)
    }
    return { round: round.name, overall: positiveOverall(round).pct, groups: byGroup }
  })
  return { rows, series }
}

/** Whose answers are read: everyone, the Board alone, or everyone else. */
export type Audience = 'everyone' | 'board' | 'others'

const minus = (all: Record<string, number>, part: Record<string, number> = {}) =>
  Object.fromEntries(Object.entries(all).map(([key, n]) => [key, n - (part[key] ?? 0)]))

/** A round as one audience gave it: its answers, and who was asked and
 *  answered by department, so every figure on the page follows. */
export function forAudience(round: SurveyRoundResults, audience: Audience): SurveyRoundResults {
  if (audience === 'everyone') return round
  const board = audience === 'board'
  return {
    ...round,
    invited: board ? { ...round.invitedBoard } : minus(round.invited, round.invitedBoard),
    responded: board ? { ...round.respondedBoard } : minus(round.responded, round.respondedBoard),
    submissions: round.submissions.filter(s => !!s.board === board),
  }
}

/** Whether any round of a survey was sent to Board members. */
export const hasBoard = (results: SurveyTypeResults) =>
  results.rounds.some(r => Object.values(r.invitedBoard ?? {}).some(n => n > 0) || r.submissions.some(s => s.board))

/** The groups anyone was asked from in a round: a partner team or department
 *  nobody was sent the survey from has nothing to show. */
export const askedGroups = (round: SurveyRoundResults): Group[] =>
  groups(round).filter(g => responseRate(round, g.departmentIds).invited > 0
    || round.submissions.some(s => g.departmentIds.includes(slotOf(s))))

/** "+24" / "-8" / "0", "-" when nothing was counted. */
export const formatNps = (n: Nps | null) =>
  !n || n.score === null ? '-' : n.score > 0 ? `+${n.score}` : String(n.score)

/** A short column label for a question: its title, or what it asks without
 *  "{partner}" when it has none. */
export function shortLabel(q: SurveyQuestion, max = 28): string {
  if (q.title.trim()) return q.title.trim()
  const text = q.text.replace(/\s*\{partner\}\s*/g, ' ').replace(/\s+/g, ' ').trim()
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text
}
